import React from "react";
import { Link } from "react-router-dom";
import { Droplets, Mail, Phone, MapPin } from "lucide-react";
import { SERVICES, INDUSTRIES, COMPANY } from "@/data/content";
import { trackEmailClick } from "@/lib/analytics";

export default function Footer() {
  return (
    <footer className="bg-navy-900 text-slate-300" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-amber-500">
                <Droplets className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="leading-none">
                <span className="block font-heading text-lg font-extrabold tracking-tight text-white">Wasserlab</span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-500">EHS Remote</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              U.S. Environmental Compliance Support — Delivered by a Specialized Global Team. Wasserlab provides remote
              environmental compliance support to U.S. clients through its international delivery team.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <a href={`mailto:${COMPANY.email}`} onClick={() => trackEmailClick("footer")} className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors" data-testid="footer-email">
                <Mail className="h-4 w-4" aria-hidden="true" /> {COMPANY.email}
              </a>
              <span className="flex items-center gap-2 text-slate-400">
                <Phone className="h-4 w-4" aria-hidden="true" /> {COMPANY.usPhone || "Phone number coming soon"}
              </span>
              <span className="flex items-start gap-2 text-slate-400">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> India Delivery Center: {COMPANY.indiaAddress}
              </span>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Services</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-slate-400 hover:text-white transition-colors" data-testid={`footer-service-${s.slug}`}>
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Industries</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {INDUSTRIES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/industries/${s.slug}`} className="text-slate-400 hover:text-white transition-colors" data-testid={`footer-industry-${s.slug}`}>
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Company</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-about">About</Link></li>
              <li><Link to="/for-consultants" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-consultants">For Consultants</Link></li>
              <li><Link to="/how-it-works" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-how">How It Works</Link></li>
              <li><Link to="/trust-security" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-trust">Trust &amp; Security</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-resources">Resources</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/free-tier-ii-assessment" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-assessment">Free Assessment</Link></li>
              <li><Link to="/privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/disclaimer" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8">
          <p className="text-xs leading-relaxed text-slate-500">
            Wasserlab provides technical and compliance preparation support. It is not a government agency or regulatory
            authority and does not provide legal advice. Final regulatory determinations and submissions remain subject to
            client review and applicable federal, state and local requirements.
          </p>
          <p className="mt-4 text-xs text-slate-500">© {new Date().getFullYear()} Wasserlab. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
